/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { ReportType } from 'app/shared/model/enumerations/report-type.model';
import { Gender } from 'app/shared/model/enumerations/gender.model';
import { GrowthChartType } from 'app/shared/model/enumerations/growth-chart-type.model';
import { AgeUnit } from 'app/shared/model/enumerations/age-unit.model';
import { createEntity, getEntity, reset, updateEntity } from './growth-legend.reducer';

export const GrowthLegendUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const growthLegendEntity = useAppSelector(state => state.growthLegend.entity);
  const loading = useAppSelector(state => state.growthLegend.loading);
  const updating = useAppSelector(state => state.growthLegend.updating);
  const updateSuccess = useAppSelector(state => state.growthLegend.updateSuccess);
  const reportTypeValues = Object.keys(ReportType);
  const genderValues = Object.keys(Gender);
  const growthChartTypeValues = Object.keys(GrowthChartType);
  const ageUnitValues = Object.keys(AgeUnit);

  const handleClose = () => {
    navigate(`/growth-legend${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    // Helper function to convert values to numbers if necessary
    const toNumberIfDefined = value => (value !== undefined && typeof value !== 'number' ? Number(value) : value);

    // List of all fields that should be converted to numbers if necessary
    const numberFields = [
      'ageFrom',
      'ageTo',
      'lambda',
      'mean',
      'sigma',
      'delta',
      'p01',
      'p1',
      'p3',
      'p5',
      'p10',
      'p15',
      'p25',
      'p50',
      'p75',
      'p85',
      'p90',
      'p95',
      'p97',
      'p99',
      'p999',
      'SD4neg',
      'SD3neg',
      'SD2neg',
      'SD1neg',
      'SD0',
      'SD1',
      'SD2',
      'SD3',
      'SD4',
      'timestampentry',
      'microSec',
      'lastupdated',
      'cursor',
      'publishedYear',
    ];

    // Apply the conversion to each field in the values object
    numberFields.forEach(field => {
      values[field] = toNumberIfDefined(values[field]);
    });

    // Construct the entity with the merged values
    const entity = {
      ...growthLegendEntity,
      ...values,
    };

    // Dispatch the appropriate action based on `isNew` status
    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          reportType: 'HEIGHT_FOR_AGE',
          gender: 'NA',
          chartType: 'ZSCORE',
          ageUnit: 'DAYS',
          ...growthLegendEntity,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.growthLegend.home.createOrEditLabel" data-cy="GrowthLegendCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.growthLegend.home.createOrEditLabel">Create or edit a GrowthLegend</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="growth-legend-id"
                  label={translate('tinyMedicRestApp.growthLegend.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.reportType')}
                id="growth-legend-reportType"
                name="reportType"
                data-cy="reportType"
                type="select"
              >
                {reportTypeValues.map(reportType => (
                  <option value={reportType} key={reportType}>
                    {translate(`tinyMedicRestApp.ReportType.${reportType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.gender')}
                id="growth-legend-gender"
                name="gender"
                data-cy="gender"
                type="select"
              >
                {genderValues.map(gender => (
                  <option value={gender} key={gender}>
                    {translate(`tinyMedicRestApp.Gender.${gender}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.chartType')}
                id="growth-legend-chartType"
                name="chartType"
                data-cy="chartType"
                type="select"
              >
                {growthChartTypeValues.map(growthChartType => (
                  <option value={growthChartType} key={growthChartType}>
                    {translate(`tinyMedicRestApp.GrowthChartType.${growthChartType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.ageFrom')}
                id="growth-legend-ageFrom"
                name="ageFrom"
                data-cy="ageFrom"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.ageTo')}
                id="growth-legend-ageTo"
                name="ageTo"
                data-cy="ageTo"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.ageUnit')}
                id="growth-legend-ageUnit"
                name="ageUnit"
                data-cy="ageUnit"
                type="select"
              >
                {ageUnitValues.map(ageUnit => (
                  <option value={ageUnit} key={ageUnit}>
                    {translate(`tinyMedicRestApp.AgeUnit.${ageUnit}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.lambda')}
                id="growth-legend-lambda"
                name="lambda"
                data-cy="lambda"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.mean')}
                id="growth-legend-mean"
                name="mean"
                data-cy="mean"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.sigma')}
                id="growth-legend-sigma"
                name="sigma"
                data-cy="sigma"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.delta')}
                id="growth-legend-delta"
                name="delta"
                data-cy="delta"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p01')}
                id="growth-legend-p01"
                name="p01"
                data-cy="p01"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p1')}
                id="growth-legend-p1"
                name="p1"
                data-cy="p1"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p3')}
                id="growth-legend-p3"
                name="p3"
                data-cy="p3"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p5')}
                id="growth-legend-p5"
                name="p5"
                data-cy="p5"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p10')}
                id="growth-legend-p10"
                name="p10"
                data-cy="p10"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p15')}
                id="growth-legend-p15"
                name="p15"
                data-cy="p15"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p25')}
                id="growth-legend-p25"
                name="p25"
                data-cy="p25"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p50')}
                id="growth-legend-p50"
                name="p50"
                data-cy="p50"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p75')}
                id="growth-legend-p75"
                name="p75"
                data-cy="p75"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p85')}
                id="growth-legend-p85"
                name="p85"
                data-cy="p85"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p90')}
                id="growth-legend-p90"
                name="p90"
                data-cy="p90"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p95')}
                id="growth-legend-p95"
                name="p95"
                data-cy="p95"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p97')}
                id="growth-legend-p97"
                name="p97"
                data-cy="p97"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p99')}
                id="growth-legend-p99"
                name="p99"
                data-cy="p99"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.p999')}
                id="growth-legend-p999"
                name="p999"
                data-cy="p999"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.SD4neg')}
                id="growth-legend-SD4neg"
                name="SD4neg"
                data-cy="SD4neg"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.SD3neg')}
                id="growth-legend-SD3neg"
                name="SD3neg"
                data-cy="SD3neg"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.SD2neg')}
                id="growth-legend-SD2neg"
                name="SD2neg"
                data-cy="SD2neg"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.SD1neg')}
                id="growth-legend-SD1neg"
                name="SD1neg"
                data-cy="SD1neg"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.SD0')}
                id="growth-legend-SD0"
                name="SD0"
                data-cy="SD0"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.SD1')}
                id="growth-legend-SD1"
                name="SD1"
                data-cy="SD1"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.SD2')}
                id="growth-legend-SD2"
                name="SD2"
                data-cy="SD2"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.SD3')}
                id="growth-legend-SD3"
                name="SD3"
                data-cy="SD3"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.SD4')}
                id="growth-legend-SD4"
                name="SD4"
                data-cy="SD4"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.active')}
                id="growth-legend-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.timestampentry')}
                id="growth-legend-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.microSec')}
                id="growth-legend-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.lastupdated')}
                id="growth-legend-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.cursor')}
                id="growth-legend-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.growthLegend.publishedYear')}
                id="growth-legend-publishedYear"
                name="publishedYear"
                data-cy="publishedYear"
                type="text"
              />
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/growth-legend" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default GrowthLegendUpdate;
